// PORTAIS — onde a Hydrodata está cadastrada como fornecedora (2026-09-03).
//
// A tela tem uma regra de conduta antes de ter funcionalidade, herdada do
// Cofre: **a senha nunca fica na tela**. Não há coluna de senha, não há
// "mostrar sempre", não há cache no navegador. Revelar é um ato deliberado,
// some sozinho em 30 segundos, e quem revelou fica registrado — a pessoa vê
// esse aviso NA HORA, porque ninguém deve descobrir depois que foi auditado.
//
// Ver a tela e ver a senha são coisas DIFERENTES (decisão do Lucas em 03/09):
// quem revela está na LISTA de `comercial.portais_acesso`, editada no botão
// 🔐 Quem vê as senhas. Não é permissão de perfil — ver ModalQuemVeSenhas.tsx.
// É o servidor que decide (`pode_revelar` da listagem); a tela só desenha.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  App as AntApp, Alert, Button, Empty, Flex, Form, Input,
  Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import {
  listarPortais, revelarSenha, salvarPortal, excluirPortal, listarAcessosPortais,
  Portal, AcessoPortal, TIPO_PORTAL, STATUS_PORTAL, TipoPortal,
} from './apiPortais';
import { SelectCliente } from '../../components/SelectCliente';
import ModalQuemVeSenhas from './ModalQuemVeSenhas';
import { fmtDataBr, dataIso } from '../../utils/dataFmt';

const { Text, Paragraph } = Typography;

// Quanto tempo a senha fica visível depois de revelada. Curto de propósito:
// tempo suficiente para copiar, curto demais para virar aba esquecida aberta.
const SEGUNDOS_VISIVEL = 30;

export default function PainelPortais() {
  const { message } = AntApp.useApp();
  const [carregando, setCarregando] = useState(true);
  const [portais, setPortais] = useState<Portal[]>([]);
  const [podeRevelar, setPodeRevelar] = useState(false);
  const [podeGerenciar, setPodeGerenciar] = useState(false);
  const [busca, setBusca] = useState('');

  // A senha revelada vive SÓ aqui, em memória, e por pouco tempo.
  const [revelado, setRevelado] = useState<{ id: number; nome: string; valor: string } | null>(null);
  const [restam, setRestam] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const [edicao, setEdicao] = useState<Partial<Portal> | null>(null);
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  const [acessos, setAcessos] = useState<AcessoPortal[] | null>(null);
  const [quemVe, setQuemVe] = useState(false);
  // O SelectCliente é ligado à mão (ver o comentário no modal); é daqui que ele
  // lê o valor que o Form guarda.
  const clienteId = Form.useWatch('cliente_id', form) as number | null | undefined;

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await listarPortais(busca ? { busca } : {});
      setPortais(r.portais || []);
      setPodeRevelar(!!r.pode_revelar);
      setPodeGerenciar(!!r.pode_gerenciar);
    } catch (e: any) {
      message.error('Falha ao carregar os portais: ' + (e.response?.data?.error || e.message));
    } finally { setCarregando(false); }
  }, [busca, message]);

  useEffect(() => { carregar(); }, [carregar]);

  // Limpa a senha da memória quando o componente sai — trocar de aba não pode
  // deixar a senha pendurada num state vivo.
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  function esconder() {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    setRevelado(null);
    setRestam(0);
  }

  async function revelar(p: Portal) {
    try {
      const r = await revelarSenha(p.id);
      esconder();
      setRevelado({ id: p.id, nome: r.nome, valor: r.valor });
      setRestam(SEGUNDOS_VISIVEL);
      timer.current = setInterval(() => {
        setRestam((n) => {
          if (n <= 1) { esconder(); return 0; }
          return n - 1;
        });
      }, 1000);
    } catch (e: any) {
      const st = e?.response?.status;
      message.error(st === 404
        ? 'Este portal não tem senha guardada.'
        : 'Não foi possível revelar: ' + (e.response?.data?.error || e.message));
    }
  }

  async function copiar(valor: string) {
    try {
      await navigator.clipboard.writeText(valor);
      message.success('Copiado. A área de transferência não se limpa sozinha — cole e siga.');
    } catch {
      message.warning('O navegador bloqueou a cópia. Selecione o texto à mão.');
    }
  }

  function abrirEdicao(p?: Portal) {
    setEdicao(p || {});
    // 🚨 `resetFields` antes do `setFieldsValue`: o segundo faz MERGE e não
    //    apaga o que já estava no formulário, e o Modal não tem
    //    `destroyOnClose`. Sem isto, abrir "Editar" na Solví, cancelar e clicar
    //    em "Novo portal" traria login, cliente e validade da Solví — foi
    //    exatamente assim que o Cofre criou um PIN "vencendo" que não vencia.
    form.resetFields();
    // 🚨 `dataIso()` antes do `dayjs()`: o Postgres devolve DATE como ISO com Z
    //    ("2027-05-10T00:00:00.000Z"), e `dayjs()` disso em BRT é 09/05 às 21h.
    //    O `format('YYYY-MM-DD')` do submit então grava 2027-05-09 — a validade
    //    anda um dia para trás A CADA SAVE, inclusive num save que não tocou no
    //    campo. A mesma tela mostrava as duas versões: a coluna usa fmtDataBr
    //    (que fatia e acerta) e o modal mostrava um dia a menos.
    const validade = p ? dataIso(p.validade) : null;
    form.setFieldsValue(p
      ? { ...p, senha: undefined, validade: validade ? dayjs(validade) : null }
      : { tipo: 'cliente', situacao: 'ativo' });
  }

  async function salvar() {
    let v: any;
    try { v = await form.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      const r = await salvarPortal({
        ...v,
        id: edicao?.id,
        validade: v.validade ? dayjs(v.validade).format('YYYY-MM-DD') : null,
      });
      // 🚨 Sucesso PARCIAL (cadastro entrou, a AWS recusou a senha) volta como
      //    200 com `aviso`, não como erro — e por isso o modal fecha e a lista
      //    recarrega do mesmo jeito. Tratar isso como falha deixava o modal
      //    aberto SEM o id do portal recém-criado, e o segundo clique em Salvar
      //    criava um segundo cadastro; a pessoa ficava com dois portais e não
      //    via nenhum, porque a tela nunca recarregava.
      if (r.aviso) message.warning(r.aviso, 8);
      else message.success(r.criado ? 'Portal cadastrado.'
        : r.senha_trocada ? 'Portal salvo e senha trocada.' : 'Portal salvo.');
      setEdicao(null);
      carregar();
    } catch (e: any) {
      message.error('Não foi possível salvar: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function excluir(p: Portal) {
    try {
      const r = await excluirPortal(p.id);
      // ⚠️ Não prometa "recuperável" como se houvesse um botão: a AWS guarda o
      //    segredo por 7 dias, mas o sistema apaga a etiqueta que aponta para
      //    ele — resgatar exige um administrador com acesso à conta AWS. Dizer
      //    "recuperável por 7 dias" faz a pessoa excluir com leveza e descobrir
      //    depois que não há caminho pela tela.
      message.success(r.senha_removida
        ? 'Portal e senha excluídos. A AWS ainda guarda a senha por 7 dias, mas o resgate '
          + 'só é possível por um administrador — não há como desfazer pela tela.'
        : 'Portal excluído.');
      carregar();
    } catch (e: any) {
      message.error('Não foi possível excluir: ' + (e.response?.data?.error || e.message));
    }
  }

  async function verAcessos() {
    try {
      const r = await listarAcessosPortais({ limite: 200 });
      setAcessos(r.acessos || []);
    } catch (e: any) {
      message.error('Não foi possível abrir a trilha: ' + (e.response?.data?.error || e.message));
    }
  }

  const colunas = [
    {
      title: 'Portal', dataIndex: 'nome', key: 'nome', width: '30%',
      render: (_: any, p: Portal) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <Text strong>{p.nome}</Text>
            <Tag color={TIPO_PORTAL[p.tipo]?.cor}>{TIPO_PORTAL[p.tipo]?.label}</Tag>
          </Space>
          {p.usuario_login && (
            <Text type="secondary" style={{ fontSize: 11 }}>login: {p.usuario_login}</Text>
          )}
          {p.url && (
            // rel="noreferrer": o portal do cliente não precisa saber de onde
            // o clique veio, e `noopener` evita que a aba nova alcance esta.
            <a href={p.url} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
              {p.url.replace(/^https?:\/\//, '').slice(0, 46)}
            </a>
          )}
        </Space>
      ),
    },
    {
      title: 'Cliente', dataIndex: 'cliente_nome', key: 'cliente', width: '22%',
      render: (v: string | null, p: Portal) => v
        ? <Text style={{ fontSize: 12 }}>{v}</Text>
        : <Text type="secondary" style={{ fontSize: 12 }}>
            {p.tipo === 'publico' ? '— (portal público)' : '—'}
          </Text>,
    },
    {
      title: 'Situação', dataIndex: 'status', key: 'status', width: 150,
      render: (s: string, p: Portal) => (
        <Space direction="vertical" size={0}>
          <Tag color={STATUS_PORTAL[s]?.cor}>{STATUS_PORTAL[s]?.label || s}</Tag>
          {p.validade && (
            <Text type="secondary" style={{ fontSize: 11 }}>até {fmtDataBr(p.validade)}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Senha', dataIndex: 'tem_senha', key: 'senha', width: 130,
      render: (tem: boolean, p: Portal) => tem
        ? (podeRevelar
            ? <Button size="small" onClick={() => revelar(p)}>🔓 Revelar</Button>
            : <Tooltip title="Você vê o cadastro, mas não tem permissão para ver a senha.">
                <Tag>🔒 guardada</Tag>
              </Tooltip>)
        : <Text type="secondary" style={{ fontSize: 12 }}>sem senha</Text>,
    },
    {
      title: 'Último acesso', dataIndex: 'revelado_em', key: 'revelado_em', width: 120,
      render: (v: string | null) => v
        ? <Text style={{ fontSize: 11 }}>{dayjs(v).format('DD/MM/YY HH:mm')}</Text>
        : <Text type="secondary" style={{ fontSize: 11 }}>nunca</Text>,
    },
    ...(podeGerenciar ? [{
      title: '', key: 'acoes', width: 96,
      render: (_: any, p: Portal) => (
        <Space size={4}>
          <Button size="small" type="text" onClick={() => abrirEdicao(p)}>✎</Button>
          <Popconfirm
            title="Excluir este portal?"
            description={p.tem_senha
              ? 'A senha sai do cofre e não há como trazê-la de volta pela tela.'
              : 'O cadastro será removido.'}
            okText="Excluir" cancelText="Cancelar"
            onConfirm={() => excluir(p)}
          >
            <Button size="small" type="text" danger>🗑</Button>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Toda vez que alguém revela uma senha, fica registrado"
        description={
          <Paragraph style={{ marginBottom: 0, fontSize: 12 }}>
            As senhas ficam no mesmo cofre do certificado digital — nunca no banco de dados
            do sistema. Ver o cadastro e ver a senha são permissões diferentes.
            A senha aparece por {SEGUNDOS_VISIVEL} segundos e some sozinha.
          </Paragraph>
        }
      />

      {revelado && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message={`Senha de ${revelado.nome} — some em ${restam}s`}
          description={
            <Flex gap={8} align="center" wrap>
              <Text code style={{ fontSize: 15 }}>{revelado.valor}</Text>
              <Button size="small" onClick={() => copiar(revelado.valor)}>Copiar</Button>
              <Button size="small" type="text" onClick={esconder}>Esconder agora</Button>
            </Flex>
          }
        />
      )}

      <Flex justify="space-between" align="center" gap={8} wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear placeholder="Buscar por portal, login ou cliente"
          style={{ maxWidth: 340 }} onSearch={setBusca}
        />
        <Space>
          {/* Aberto a todo mundo que vê a aba, de propósito: quem tem acesso às
              senhas da empresa não é informação restrita — esconder isso só
              esconde de quem deveria estranhar. Alterar exige a permissão, e o
              servidor confere. */}
          <Button onClick={() => setQuemVe(true)}>🔐 Quem vê as senhas</Button>
          {podeGerenciar && <Button onClick={verAcessos}>Quem acessou</Button>}
          {podeGerenciar && <Button type="primary" onClick={() => abrirEdicao()}>Novo portal</Button>}
        </Space>
      </Flex>

      <Table
        rowKey="id" size="small" loading={carregando}
        dataSource={portais} columns={colunas as any}
        tableLayout="fixed" pagination={false}
        locale={{ emptyText: (
          <Empty description="Nenhum portal cadastrado ainda" />
        ) }}
      />

      {/* Cadastro */}
      <Modal
        open={!!edicao} onCancel={() => setEdicao(null)} onOk={salvar}
        confirmLoading={salvando} okText="Salvar" cancelText="Cancelar"
        title={edicao?.id ? `Editar ${edicao.nome}` : 'Novo portal'}
        width="94vw" style={{ maxWidth: 720, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="nome" label="Portal" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="Ex.: Bolsa Nacional de Compras, Portal do Fornecedor Solví" />
          </Form.Item>

          <Flex gap={8} wrap>
            <Form.Item name="tipo" label="Tipo" style={{ flex: '1 1 200px' }}>
              <Select
                options={(Object.keys(TIPO_PORTAL) as TipoPortal[])
                  .map((k) => ({ value: k, label: TIPO_PORTAL[k].label }))}
              />
            </Form.Item>
            <Form.Item name="situacao" label="Situação do cadastro" style={{ flex: '1 1 200px' }}>
              <Select options={[
                { value: 'ativo', label: 'Ativo' },
                { value: 'pendente', label: 'Cadastro pendente' },
                { value: 'inativo', label: 'Inativo' },
              ]} />
            </Form.Item>
          </Flex>

          {/* ⚠️ Ligação EXPLÍCITA, e não `<Form.Item name="cliente_id">` em
              volta do SelectCliente: o Form.Item clona o filho injetando
              `value`/`onChange`, e a precedência entre o que ele injeta e o que
              o filho já tem varia entre versões do antd. Com a aposta errada, o
              campo fica preso em null para sempre. Aqui o Form guarda o valor
              num campo oculto e o Select lê dele por `useWatch`.
              O SelectCliente é o mesmo dos outros cinco seletores do sistema:
              busca por razão social, nome fantasia e dígitos do CNPJ. */}
          <Form.Item label="Cliente"
            extra="Deixe em branco em portal de licitação pública — lá o comprador muda a cada edital."
          >
            <SelectCliente
              value={clienteId ?? null}
              onChange={(v) => form.setFieldsValue({ cliente_id: v })}
              placeholder="Buscar cliente por nome ou CNPJ"
            />
          </Form.Item>
          <Form.Item name="cliente_id" hidden><Input /></Form.Item>

          <Flex gap={8} wrap>
            <Form.Item name="usuario_login" label="Login (pode ficar visível)" style={{ flex: '1 1 240px' }}>
              <Input placeholder="CNPJ, e-mail ou usuário" />
            </Form.Item>
            <Form.Item
              name="senha"
              label={edicao?.id ? 'Nova senha (deixe em branco para não trocar)' : 'Senha'}
              style={{ flex: '1 1 240px' }}
            >
              <Input.Password placeholder={edicao?.id && edicao?.tem_senha ? '•••••• guardada' : ''} />
            </Form.Item>
          </Flex>

          <Form.Item name="url" label="Endereço do portal">
            <Input placeholder="https://..." />
          </Form.Item>

          <Flex gap={8} wrap>
            <Form.Item
              name="validade" label="Cadastro válido até"
              style={{ flex: '1 1 200px' }}
              extra="Aparece em vermelho na lista quando vencer. Não gera e-mail."
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="responsavel_email" label="Responsável" style={{ flex: '1 1 240px' }}>
              <Input placeholder="quem cuida deste cadastro" />
            </Form.Item>
          </Flex>

          <Form.Item
            name="observacao" label="Observação"
            extra="Dica de contexto — nunca a senha, nem a resposta da pergunta secreta. Este campo não é cofre: fica em texto e não registra quem leu."
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Trilha */}
      <Modal
        open={!!acessos} onCancel={() => setAcessos(null)} footer={null}
        title="Quem mexeu nas senhas dos portais"
        width="94vw" style={{ maxWidth: 900, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Table
          rowKey="id" size="small" pagination={false} dataSource={acessos || []}
          locale={{ emptyText: <Empty description="Nada registrado ainda" /> }}
          columns={[
            { title: 'Quando', dataIndex: 'em', width: 140,
              render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm') },
            { title: 'Quem', dataIndex: 'usuario_email', width: 220 },
            { title: 'O quê', dataIndex: 'acao', width: 110,
              render: (a: string) => <Tag color={a === 'revelou' ? 'orange' : 'default'}>{a}</Tag> },
            { title: 'Portal', dataIndex: 'rotulo_snap',
              render: (v: string, a: AcessoPortal) => (
                <Space size={4}>
                  <Text style={{ fontSize: 12 }}>{v}</Text>
                  {a.segredo_id === null && <Tag>excluído</Tag>}
                </Space>
              ) },
            { title: 'Motivo', dataIndex: 'detalhe' },
          ] as any}
        />
      </Modal>

      {/* Quem pode revelar as senhas. `aoMudar` recarrega a lista porque tirar
          o PRÓPRIO acesso tem que fazer o botão Revelar sumir na hora — sem
          isso a tela seguiria oferecendo um botão que o servidor recusa. */}
      <ModalQuemVeSenhas
        aberto={quemVe}
        aoFechar={() => setQuemVe(false)}
        aoMudar={() => { esconder(); carregar(); }}
      />
    </div>
  );
}
